import { useWeb3React } from '@web3-react/core'
import { Chain, Currency, TokenBalance, TokenStandard } from 'graphql/data/__generated__/types-and-hooks'
import { CHAIN_ID_TO_BACKEND_NAME } from 'graphql/data/util'
import { useFetchedTokenPriceChanges } from 'graphql/thegraph/tokens/tokenData'
import { useEffect, useMemo, useState } from 'react'
import { getPercentChange } from 'utils/thegraph'

import { useAllTokenBalances, useNativeCurrencyBalances } from '../state/connection/hooks'
import { useEthPrices } from './useEthPrices'

const PORTFOLIO_LOADING_TIMEOUT_MS = 5_000

export default function usePortfoliosListTokens(walletAddress: string) {
  const { chainId } = useWeb3React()
  const xdcPrice = useEthPrices()

  const walletBalances = useNativeCurrencyBalances([walletAddress])
  const nativeBalance = walletBalances[walletAddress]
  const [allTokenBalances, tokenBalancesLoading] = useAllTokenBalances()
  const [portfolioLoadingTimedOut, setPortfolioLoadingTimedOut] = useState(false)

  useEffect(() => {
    setPortfolioLoadingTimedOut(false)
    if (!tokenBalancesLoading && nativeBalance) return undefined

    const timeout = setTimeout(() => setPortfolioLoadingTimedOut(true), PORTFOLIO_LOADING_TIMEOUT_MS)
    return () => clearTimeout(timeout)
  }, [nativeBalance, tokenBalancesLoading, walletAddress])

  const heldTokenAddresses = useMemo(
    () =>
      Object.values(allTokenBalances).flatMap((tokenAmount) =>
        tokenAmount && tokenAmount.greaterThan(0) ? [tokenAmount.currency.address.toLowerCase()] : []
      ),
    [allTokenBalances]
  )
  const { data: tokenDataMap } = useFetchedTokenPriceChanges(heldTokenAddresses)

  const chain = chainId ? CHAIN_ID_TO_BACKEND_NAME[chainId] : Chain.Xinfin
  const nativeTokenValue = nativeBalance && xdcPrice ? Number(nativeBalance.toExact()) * xdcPrice.current : 0
  const nativeTokenChange = xdcPrice ? getPercentChange(xdcPrice.current.toString(), xdcPrice.oneDay.toString()) : 0

  const tokenBalances = useMemo<TokenBalance[]>(() => {
    const nativeTokenBalance: TokenBalance[] = nativeBalance
      ? [
          {
            __typename: 'TokenBalance',
            id: `${chain}-NATIVE`,
            ownerAddress: walletAddress,
            quantity: nativeBalance.quotient.toString() as unknown as number,
            denominatedValue: {
              __typename: 'Amount',
              id: `${chain}-NATIVE-value`,
              currency: Currency.Usd,
              value: nativeTokenValue,
            },
            tokenProjectMarket: {
              __typename: 'TokenProjectMarket',
              id: `${chain}-NATIVE-market`,
              currency: Currency.Usd,
              pricePercentChange: {
                __typename: 'Amount',
                id: `${chain}-NATIVE-change`,
                value: nativeTokenChange,
              },
              tokenProject: {
                __typename: 'TokenProject',
                id: `${chain}-NATIVE-project`,
                tokens: [],
              },
            },
            token: {
              __typename: 'Token',
              id: `${chain}-NATIVE`,
              chain,
              address: 'NATIVE',
              name: 'XDC',
              symbol: 'XDC',
              standard: TokenStandard.Native,
              decimals: 18,
            },
          },
        ]
      : []

    const activeTokenBalances = Object.values(allTokenBalances).flatMap<TokenBalance>((tokenAmount) => {
      if (!tokenAmount || !tokenAmount.greaterThan(0)) {
        return []
      }

      const token = tokenAmount.currency
      const tokenData = tokenDataMap?.[token.address.toLowerCase()]
      const denominatedValue = tokenData?.priceUSD
        ? {
            __typename: 'Amount' as const,
            id: `${chain}-${token.address}-value`,
            currency: Currency.Usd,
            value: Number(tokenAmount.toExact()) * tokenData.priceUSD,
          }
        : undefined
      return [
        {
          __typename: 'TokenBalance',
          id: `${chain}-${token.address}`,
          ownerAddress: walletAddress,
          quantity: tokenAmount.quotient.toString() as unknown as number,
          denominatedValue,
          tokenProjectMarket: {
            __typename: 'TokenProjectMarket',
            id: `${chain}-${token.address}-market`,
            currency: Currency.Usd,
            pricePercentChange: {
              __typename: 'Amount',
              id: `${chain}-${token.address}-change`,
              value: tokenData?.priceUSDChange ?? 0,
            },
            tokenProject: {
              __typename: 'TokenProject',
              id: `${chain}-${token.address}-project`,
              tokens: [],
            },
          },
          token: {
            __typename: 'Token',
            id: `${chain}-${token.address}`,
            chain,
            address: token.address,
            name: token.name,
            symbol: token.symbol,
            standard: TokenStandard.Erc20,
            decimals: token.decimals,
          },
        },
      ]
    })

    return [...nativeTokenBalance, ...activeTokenBalances]
  }, [allTokenBalances, chain, nativeBalance, nativeTokenChange, nativeTokenValue, tokenDataMap, walletAddress])

  const totalCurrentBalance = useMemo(
    () => tokenBalances.reduce((total, item) => total + (item.denominatedValue?.value || 0), 0),
    [tokenBalances]
  )

  const totalBalanceChange = useMemo(
    () =>
      tokenBalances.reduce((total, item) => {
        const denominatedValue = item.denominatedValue?.value || 0
        const pricePercentChange = item.tokenProjectMarket?.pricePercentChange?.value || 0
        const valueBeforeChange = denominatedValue / (1 + pricePercentChange / 100)
        return total + denominatedValue - valueBeforeChange
      }, 0),
    [tokenBalances]
  )

  const totalValueBeforeChange = totalCurrentBalance - totalBalanceChange
  const totalPercentChange = totalValueBeforeChange === 0 ? 0 : (totalBalanceChange / totalValueBeforeChange) * 100
  const isLoading = (tokenBalancesLoading || !nativeBalance) && !portfolioLoadingTimedOut

  return { isLoading, tokenBalances, totalCurrentBalance, totalBalanceChange, totalPercentChange }
}
